/**
 * Toast Context - Using react-hot-toast
 */
import React, { createContext, useContext } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const showToast = (message, type = 'default') => {
    switch (type) {
      case 'success':
        toast.success(message, {
          icon: <CheckCircle size={20} className="text-green-600" />,
        });
        break;
      case 'error':
        toast.error(message, {
          icon: <XCircle size={20} className="text-red-600" />,
        });
        break;
      case 'warning':
        toast(message, { 
          icon: <AlertTriangle size={20} className="text-amber-600" />,
          style: {
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '12px',
            color: '#1e293b',
            boxShadow: '0 8px 32px 0 rgba(245, 158, 11, 0.15)',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
          },
        });
        break;
      case 'info':
        toast(message, { 
          icon: <Info size={20} className="text-blue-600" />,
          style: {
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            color: '#1e293b',
            boxShadow: '0 8px 32px 0 rgba(59, 130, 246, 0.15)',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
          },
        });
        break;
      default:
        toast(message);
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.7)',
            borderRadius: '12px',
            color: '#1e293b',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
          },
          success: {
            duration: 3000,
            style: {
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px',
              color: '#1e293b',
              boxShadow: '0 8px 32px 0 rgba(34, 197, 94, 0.15)',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '500',
            },
          },
          error: {
            duration: 4000,
            style: {
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              color: '#1e293b',
              boxShadow: '0 8px 32px 0 rgba(239, 68, 68, 0.15)',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '500',
            },
          },
        }}
        containerStyle={{
          top: 20,
          right: 20,
        }}
      />
      {children}
    </ToastContext.Provider>
  );
};

